import { ChipInput } from '@/components/ChipInput'
import { InfoTooltip } from '@/components/InfoTooltip'
import { RequiredKeywordGroups } from '@/components/RequiredKeywordGroups'
import type { DocumentRequirementRules } from '@/types/bundle'

export interface CaseDataRequirementsSectionProps {
  rules: DocumentRequirementRules
  onChange: (rules: DocumentRequirementRules) => void
}

// Structurally identical to document_requirements.rules
// (app/engines/request_validator.py's _check_case_data reads must_have_all/
// must_have_any_of the same way), but there is no catalogue of valid
// case-data key names anywhere in the platform - unlike
// DocumentRequirementsSection (which picks from document_types.json via a
// modal), entries here are free-text: a direct ChipInput for must_have_all,
// and the shared RequiredKeywordGroups (promoted from document-types, same
// AND-across-groups/OR-within-group string[][] shape) for must_have_any_of.
export function CaseDataRequirementsSection({ rules, onChange }: CaseDataRequirementsSectionProps) {
  const mustHaveAll = rules.must_have_all ?? []
  const mustHaveAnyOf = rules.must_have_any_of ?? []

  return (
    <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
      <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
        Case data requirements
        <InfoTooltip text="Extra data the caller must send with the request itself, separate from the uploaded documents." />
      </h2>
      <p className="text-meta text-text-secondary">
        Case-data keys the caller must supply on the request, checked the same way as document
        requirements - there's no fixed list of valid keys, so type any key name the caller is
        expected to send.
      </p>

      <div className="flex flex-col gap-1">
        <span className="text-meta font-medium text-text-secondary">Must have all</span>
        <ChipInput
          value={mustHaveAll}
          onChange={(must_have_all) => onChange({ ...rules, must_have_all })}
          placeholder="Type a case-data key, press Enter…"
        />
      </div>

      <RequiredKeywordGroups
        groups={mustHaveAnyOf}
        onChange={(must_have_any_of) => onChange({ ...rules, must_have_any_of })}
        itemNoun="case-data key"
        minGroups={0}
        description={
          <>
            A request must supply at least one key in <em>every</em> group below (groups are combined
            with AND). Within a group, any one key is enough (they're combined with OR).
          </>
        }
      />
    </section>
  )
}
